import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog";
import { TaskTable } from "@/components/tasks/TaskTable";
import { DonutCard, BarCard, TrendCard } from "@/components/tasks/TaskCharts";
import { importanceMeta, IMPORTANCE_KEYS } from "@/lib/taskUi";
import {
  Plus, Star, HelpCircle, CheckSquare, AlertTriangle, Clock, CalendarDays, ListTodo,
  Send, LayoutDashboard, ArrowUp, ArrowDown, X, Users,
} from "lucide-react";
import { toast } from "sonner";

const VIEWS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "top10", label: "Daily Top 10", icon: Star },
  { key: "today", label: "Today", icon: Clock },
  { key: "overdue", label: "Overdue", icon: AlertTriangle },
  { key: "upcoming", label: "Upcoming", icon: CalendarDays },
  { key: "all", label: "All Active", icon: ListTodo },
  { key: "completed", label: "Completed", icon: CheckSquare },
  { key: "assigned", label: "Assigned By Me", icon: Send },
];

export default function Tasks() {
  const { isManager } = useAuth();
  const [view, setView] = useState("dashboard");
  const [add, setAdd] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div>
      <PageHeader title="Tasks" description="Plan, prioritize and track your work and your team's."
        actions={<Button className="bg-brand hover:bg-brand-strong" onClick={() => setAdd(true)} data-testid="add-task-button"><Plus className="w-4 h-4 mr-1.5" /> Add Task</Button>} />

      <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto tr-scroll" data-testid="task-view-tabs">
        {VIEWS.filter((v) => v.key !== "dashboard" || true).map((v) => (
          <button key={v.key} data-testid={`taskview-${v.key}`} onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${view === v.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <v.icon className="w-4 h-4" /> {v.label}
          </button>
        ))}
      </div>

      {view === "dashboard" && <Dashboard isManager={isManager} key={`d${refreshKey}`} />}
      {view === "top10" && <DailyTop10 key={`t${refreshKey}`} onChanged={refresh} />}
      {["today", "overdue", "upcoming", "all", "completed"].includes(view) && <TaskListView view={view} key={`l${view}${refreshKey}`} onChanged={refresh} />}
      {view === "assigned" && <AssignedByMe key={`a${refreshKey}`} onChanged={refresh} />}

      <TaskFormDialog open={add} onClose={() => setAdd(false)} onCreated={() => { refresh(); }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testId }) {
  const tones = { rose: "bg-rose-100 text-rose-600", amber: "bg-amber-100 text-amber-600",
    sky: "bg-sky-100 text-sky-600", emerald: "bg-emerald-100 text-emerald-600", indigo: "bg-indigo-100 text-indigo-600" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-4.5 h-4.5" /></div>
      <p className="mt-3 text-2xl font-display font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function Dashboard({ isManager }) {
  const [s, setS] = useState(null);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    api.get("/tasks/summary").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e)));
    if (isManager) api.get("/tasks/team-summary").then((r) => setTeam(r.data)).catch(() => {});
  }, [isManager]);
  if (!s) return <PageLoader />;
  const impData = s.by_importance.map((x) => ({ name: importanceMeta(x.importance).label, key: x.importance, value: x.count }));
  const catData = s.by_category.slice(0, 8).map((x) => ({ name: x.category, value: x.count }));
  return (
    <div className="space-y-5" data-testid="task-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Clock} label="Due Today" value={s.due_today} tone="amber" testId="stat-due-today" />
        <StatCard icon={AlertTriangle} label="Overdue" value={s.overdue} tone="rose" testId="stat-overdue" />
        <StatCard icon={CalendarDays} label="Upcoming (7d)" value={s.upcoming_week} tone="sky" testId="stat-upcoming" />
        <StatCard icon={CheckSquare} label="Done (7d)" value={s.completed_week} tone="emerald" testId="stat-completed" />
        <StatCard icon={Send} label="Assigned by me" value={s.assigned_by_me_open} tone="indigo" testId="stat-assigned" />
        <StatCard icon={ListTodo} label="My Projects" value={s.my_projects} tone="sky" testId="stat-projects" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <DonutCard title="Tasks by Importance" data={impData} colorByImportance testId="chart-importance" />
        <BarCard title="Tasks by Category" data={catData} testId="chart-category" />
        <TrendCard title="Completion Trend (7 days)" data={s.completion_trend} testId="chart-trend" />
      </div>

      {isManager && team && (
        <div className="space-y-4 pt-2" data-testid="manager-dashboard">
          <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-brand" /> Team Overview</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Clock} label="Team Due Today" value={team.team_due_today} tone="amber" testId="team-stat-due" />
            <StatCard icon={AlertTriangle} label="Team Overdue" value={team.team_overdue} tone="rose" testId="team-stat-overdue" />
            <StatCard icon={ListTodo} label="Team Open" value={team.team_open} tone="sky" testId="team-stat-open" />
            <StatCard icon={CheckSquare} label="Completion Rate" value={`${team.completion_rate}%`} tone="emerald" testId="team-stat-rate" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TrendCard title="Assigned vs Completed (7 days)" data={team.trend} testId="team-trend"
              lines={[{ key: "created", color: "#0093D6" }, { key: "completed", color: "#10B981" }]} />
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="workload-table">
              <p className="text-sm font-semibold text-slate-700 mb-2">Workload by Team Member</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-slate-400 text-left border-b border-slate-100">
                    <th className="py-1.5 pr-2">Member</th><th className="px-1">Open</th><th className="px-1">Crit</th>
                    <th className="px-1">High</th><th className="px-1">Overdue</th><th className="px-1">Today</th><th className="px-1">Done/wk</th></tr></thead>
                  <tbody>
                    {team.workload.filter((w) => w.open || w.completed_week).map((w) => (
                      <tr key={w.user_id} className="border-b border-slate-50" data-testid={`workload-${w.user_id}`}>
                        <td className="py-1.5 pr-2 font-medium text-slate-700 truncate max-w-[140px]">{w.name}</td>
                        <td className="px-1 text-center">{w.open}</td>
                        <td className="px-1 text-center text-rose-600">{w.critical}</td>
                        <td className="px-1 text-center text-amber-600">{w.high}</td>
                        <td className="px-1 text-center text-rose-500">{w.overdue}</td>
                        <td className="px-1 text-center">{w.due_today}</td>
                        <td className="px-1 text-center text-emerald-600">{w.completed_week}</td>
                      </tr>
                    ))}
                    {team.workload.every((w) => !w.open && !w.completed_week) && <tr><td colSpan={7} className="text-center text-slate-400 py-4">No team tasks yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {(team.projects_at_risk.length > 0 || team.upcoming_milestones.length > 0) && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="projects-at-risk">
                <p className="text-sm font-semibold text-slate-700 mb-2">Projects At Risk</p>
                {team.projects_at_risk.length ? team.projects_at_risk.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="text-slate-700">{p.name}</span>
                    <span className="text-xs text-rose-600">{p.overdue_tasks} overdue</span>
                  </div>
                )) : <p className="text-xs text-slate-400">None — all on track.</p>}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="upcoming-milestones">
                <p className="text-sm font-semibold text-slate-700 mb-2">Upcoming Milestones</p>
                {team.upcoming_milestones.length ? team.upcoming_milestones.map((m) => (
                  <div key={m.id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="text-slate-700">{m.title}</span>
                    <span className="text-xs text-slate-400">{m.due_date ? new Date(m.due_date).toLocaleDateString() : "—"}</span>
                  </div>
                )) : <p className="text-xs text-slate-400">None scheduled.</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DailyTop10({ onChanged }) {
  const [data, setData] = useState(null);
  const [help, setHelp] = useState(false);
  const navigate = useNavigate();
  const open = (id) => navigate(`/tasks/detail/${id}`, { state: { from: "/tasks", label: "Tasks" } });
  const load = useCallback(() => api.get("/tasks/top10").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageLoader />;

  const move = async (idx, dir) => {
    const ids = data.items.map((t) => t.id);
    const j = idx + dir; if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.post("/tasks/top10/reorder", { ordered_ids: ids }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (id, on) => {
    try { await api.post(`/tasks/${id}/top10`, { on }); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const complete = async (id) => { try { await api.patch(`/tasks/${id}`, { status: "completed" }); load(); onChanged?.(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="daily-top10">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
        <h3 className="font-display font-semibold text-slate-800">Daily Top 10</h3>
        <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2">{data.count}/{data.max}</span>
        <button onClick={() => setHelp((h) => !h)} data-testid="top10-help" className="text-slate-400 hover:text-brand"><HelpCircle className="w-4 h-4" /></button>
      </div>
      {help && (
        <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="top10-help-text">
          Your Daily Top 10 is a focused list of the most important activities to complete today. Use it to prioritize the work with the greatest impact before moving on to lower-priority tasks. Tasks sort by importance, then by overdue and due timing.
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-6">
        {data.items.length ? data.items.map((t, i) => {
          const im = importanceMeta(t.importance);
          return (
            <div key={t.id} data-testid={`top10-item-${t.id}`} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
              <span className="text-xs font-bold text-slate-300 w-5">{i + 1}</span>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-300 hover:text-brand disabled:opacity-30" data-testid={`top10-up-${t.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, 1)} disabled={i === data.items.length - 1} className="text-slate-300 hover:text-brand disabled:opacity-30" data-testid={`top10-down-${t.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
              <button onClick={() => complete(t.id)} data-testid={`top10-complete-${t.id}`} className="w-5 h-5 rounded-full border-2 border-slate-300 hover:border-emerald-500 shrink-0" />
              <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${im.badge}`}><im.icon className="w-3 h-3 mr-0.5" />{im.label}</span>
              <button type="button" onClick={() => open(t.id)} className="min-w-0 flex-1 text-left group" data-testid={`top10-open-${t.id}`}><p className="text-sm font-medium text-slate-800 truncate group-hover:text-brand group-hover:underline">{t.title}</p>
                <p className="text-xs text-slate-400 truncate">{[t.category_label, t.person_name].filter(Boolean).join(" · ")}</p></button>
              {t.is_overdue && <span className="text-xs font-semibold text-rose-600">Overdue {t.days_overdue}d</span>}
              <span className="text-xs text-slate-500 hidden sm:block">{t.due_date ? new Date(t.due_date).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" }) : ""}</span>
              <button onClick={() => toggle(t.id, false)} className="text-slate-300 hover:text-rose-500" data-testid={`top10-remove-${t.id}`}><X className="w-4 h-4" /></button>
            </div>
          );
        }) : <EmptyState icon={Star} title="No Top 10 tasks yet" description="Add up to 10 of today's most important tasks below." />}
      </div>

      {data.suggestions.length > 0 && data.count < data.max && (
        <div data-testid="top10-suggestions">
          <p className="text-xs font-semibold text-slate-500 mb-2">Suggested for today</p>
          <div className="rounded-xl border border-dashed border-slate-300 bg-white">
            {data.suggestions.map((t) => {
              const im = importanceMeta(t.importance);
              return (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0" data-testid={`top10-suggestion-${t.id}`}>
                  <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${im.badge}`}><im.icon className="w-3 h-3 mr-0.5" />{im.label}</span>
                  <button type="button" onClick={() => open(t.id)} className="text-sm text-slate-700 flex-1 truncate text-left hover:text-brand hover:underline" data-testid={`top10-suggestion-open-${t.id}`}>{t.title}</button>
                  {t.is_overdue && <span className="text-xs text-rose-600">Overdue</span>}
                  <Button size="sm" variant="outline" onClick={() => toggle(t.id, true)} data-testid={`top10-add-${t.id}`}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskListView({ view, onChanged }) {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ category: "", importance: "", status: "", assignee_id: "__me__" });
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    const params = {};
    if (view === "today") params.due = "today";
    if (view === "overdue") params.overdue = "true";
    if (view === "upcoming") params.due = "upcoming";
    if (view === "completed") params.status = "completed";
    if (view === "all") { params.status = ""; }
    if (view === "all") { Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; }); }
    try {
      if (["today", "overdue", "upcoming", "completed"].includes(view)) {
        const { data } = await api.get("/tasks/mine");
        const map = { today: data.due_today, overdue: data.overdue, upcoming: data.upcoming, completed: data.completed };
        setRows(map[view]);
      } else {
        const { data } = await api.get("/tasks", { params });
        setRows((data.tasks || []).filter((t) => t.status !== "completed" && t.status !== "cancelled"));
      }
    } catch (e) { toast.error(apiError(e)); setRows([]); }
  }, [view, filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (view === "all") Promise.all([api.get("/task-categories"), api.get("/tasks/assignable-users")])
      .then(([c, u]) => { setCats(c.data.items); setUsers(u.data.items); }).catch(() => {});
  }, [view]);
  if (!rows) return <PageLoader />;

  return (
    <div>
      {view === "all" && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="task-filters">
          <FSelect value={filters.importance} onChange={(v) => setFilters({ ...filters, importance: v })} placeholder="All importance"
            options={IMPORTANCE_KEYS.map((k) => [k, importanceMeta(k).label])} testId="filter-importance" />
          <FSelect value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} placeholder="All categories"
            options={cats.map((c) => [c.key, c.label])} testId="filter-category" />
          <FSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} placeholder="All statuses"
            options={[["not_started", "Not Started"], ["in_progress", "In Progress"], ["waiting", "Waiting"]]} testId="filter-status" />
          <FSelect value={filters.assignee_id} onChange={(v) => setFilters({ ...filters, assignee_id: v })} placeholder="Anyone"
            options={[["__me__", "Assigned to me"], ["__unassigned__", "Not Assigned"], ...users.map((u) => [u.user_id, u.name])]} testId="filter-assignee" />
        </div>
      )}
      <TaskTable rows={rows} onChanged={() => { load(); onChanged?.(); }} showAssignee={view === "all"}
        resizeKey={view === "all" ? "tasks-all-columns" : "tasks-list-columns"} testId={`task-list-${view}`}
        empty={<div className="rounded-xl border border-slate-200 bg-white" data-testid={`task-list-${view}`}><EmptyState icon={CheckSquare} title="Nothing here" description="No tasks match this view." /></div>} />
    </div>
  );
}

function AssignedByMe({ onChanged }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/tasks/assigned-by-me").then((r) => setRows(r.data.tasks)).catch((e) => { toast.error(apiError(e)); setRows([]); }), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <PageLoader />;
  return (
    <TaskTable rows={rows} onChanged={() => { load(); onChanged?.(); }} showTop10={false} showAssignee
      resizeKey="tasks-assigned-columns" testId="assigned-by-me-list"
      empty={<div className="rounded-xl border border-slate-200 bg-white" data-testid="assigned-by-me-list"><EmptyState icon={Send} title="Nothing assigned out" description="Tasks you assign to others will appear here until completed." /></div>} />
  );
}

function FSelect({ value, onChange, placeholder, options, testId }) {
  const NONE = "__none__";
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
      <SelectTrigger className="h-9 w-44 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white max-h-64">
        <SelectItem value={NONE}>{placeholder}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
